import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';
import type Geometry from '@arcgis/core/geometry/Geometry.js';
import {
  registerHandle,
  resolve,
  summarizeRef,
  type ResolvedRef,
  type Source,
  SourceSchema,
} from '@/agent/refs';
import type { Tool } from './types';

type Input = { a: Source; b: Source };

export const computeIntersection: Tool<Input> = {
  name: 'compute_intersection',
  description:
    'Compute the geometric intersection of two Sources (geodesic). Returns a polygon handle plus area in square meters, or { empty: true } when the two are disjoint. No map side-effects — pair with `render` to draw.',
  input_schema: {
    type: 'object',
    properties: {
      a: SourceSchema,
      b: SourceSchema,
    },
    required: ['a', 'b'],
    additionalProperties: false,
  },
  execute: async ({ a, b }) => {
    const refA = await resolve(a);
    const refB = await resolve(b);
    const geomA = refA.geometry ?? refA.extent;
    const geomB = refB.geometry ?? refB.extent;
    if (!geomA) throw new Error('compute_intersection: source "a" has no geometry');
    if (!geomB) throw new Error('compute_intersection: source "b" has no geometry');

    const result = geometryEngine.intersect(
      geomA as Parameters<typeof geometryEngine.intersect>[0],
      geomB as Parameters<typeof geometryEngine.intersect>[1],
    ) as Geometry | null;

    if (!result) {
      return { empty: true, area_sq_m: 0 };
    }

    const newRef: ResolvedRef = {
      source_kind: 'geometry',
      geometry: result,
      extent: (result as { extent?: __esri.Extent }).extent ?? undefined,
    };
    const handle = registerHandle(newRef);
    const area =
      result.type === 'polygon'
        ? geometryEngine.geodesicArea(
            result as Parameters<typeof geometryEngine.geodesicArea>[0],
            'square-meters',
          )
        : 0;

    return {
      handle,
      summary: summarizeRef(newRef),
      area_sq_m: area,
    };
  },
};
